import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Mail, Plus, Trash2, UserRound } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSubscriptionAccess } from '@/hooks/use-subscription-access';
import { getErrorMessage } from '@/lib/errors';
import { teamApi, TEAM_KEY } from '@/lib/team-api';
import type { TeamMember } from '@/types/api';

/**
 * The people on this account.
 *
 * ## Everyone sees the list; only the owner changes it
 *
 * A member who cannot see who else has access to their servers is being kept in the dark about
 * something that concerns them. The controls are the owner's, and the server enforces that as well
 * — this only decides what to render.
 */
export function TeamPage() {
  const queryClient = useQueryClient();
  const { trial } = useSubscriptionAccess();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [pendingRemove, setPendingRemove] = useState<TeamMember | null>(null);

  const team = useQuery({ queryKey: TEAM_KEY, queryFn: () => teamApi.list() });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: TEAM_KEY });
  };

  const invite = useMutation({
    mutationFn: () => teamApi.invite(email.trim(), name.trim()),
    onSuccess: (result) => {
      setEmail('');
      setName('');
      setError(null);
      setNotice(result.message);
      invalidate();
    },
    onError: (e) => {
      setNotice(null);
      setError(e);
    },
  });

  const remove = useMutation({
    mutationFn: (member: TeamMember) => teamApi.remove(member.id),
    onSuccess: (result) => {
      setPendingRemove(null);
      setError(null);
      setNotice(result.message);
      invalidate();
    },
    onError: (e) => setError(e),
  });

  const data = team.data;
  const canManage = data?.can_manage ?? false;
  const quota = data?.quota;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Team</h1>
        <p className="text-muted-foreground text-sm">
          Everyone here can manage the servers, automation, monitoring and backups on this account.
        </p>
      </div>

      {error !== null && <Alert variant="danger">{getErrorMessage(error)}</Alert>}
      {notice !== null && <Alert variant="success">{notice}</Alert>}

      {canManage && (
        <Card>
          <CardHeader>
            <CardTitle>Invite someone</CardTitle>
            <CardDescription>
              They receive an email, set their own password, and sign in with their own account.
              {/* Hidden on Starter, where the limit is 1. "Your plan covers 1 people on this
                  account, including you" is ungrammatical and, worse, describes a team feature to
                  somebody whose plan has none — the amber notice below already says the places are
                  used up, which is the only part that is actionable. Kept for plans that really do
                  cover several people, and pluralised properly. */}
              {quota !== undefined && !quota.unlimited && quota.limit !== null && quota.limit > 1 && (
                <> Your plan covers {quota.limit} people on this account, including you.</>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="flex flex-wrap items-end gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                if (email.trim() !== '') invite.mutate();
              }}
            >
              <div className="min-w-[16rem] flex-1 space-y-2">
                <Label htmlFor="team-email">Email address</Label>
                <Input
                  id="team-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="off"
                />
              </div>

              <div className="min-w-[12rem] flex-1 space-y-2">
                <Label htmlFor="team-name">Name (optional)</Label>
                <Input
                  id="team-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  autoComplete="off"
                />
              </div>

              <Button
                type="submit"
                disabled={
                  invite.isPending || email.trim() === '' || quota?.permits_more === false
                }
              >
                {invite.isPending ? (
                  <Loader2 className="mr-2 size-4 animate-spin" />
                ) : (
                  <Plus className="mr-2 size-4" />
                )}
                Send invitation
              </Button>
            </form>

            {/* Authoritative, from the API — the same check the invite endpoint applies. Deriving
                "at the limit" in the browser is how the operator case gets got wrong. */}
            {quota?.permits_more === false && (
              <Alert variant="warning" className="mt-3">
                {trial?.status !== 'running'
                  ? 'You have used every place your plan allows on this account. Upgrade to add more.'
                  : quota.limit === 1
                    ? 'The free trial is for one person, so inviting others needs a plan that includes a team. Everything else works during the trial.'
                    : `The free trial covers ${quota.limit ?? 0} people on this account, and every place is taken. Choose a plan to add more.`}{' '}
                <Link to="/billing" className="font-medium underline underline-offset-2">
                  {trial?.status === 'running' ? 'See plans' : 'Go to billing'}
                </Link>
                .
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>People</CardTitle>
          <CardDescription>
            {canManage
              ? 'Removing someone signs them out immediately.'
              : 'Only the account owner can invite or remove people.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {team.isLoading && (
            <p className="text-muted-foreground flex items-center gap-2 text-sm">
              <Loader2 className="size-4 animate-spin" /> Loading…
            </p>
          )}

          {(data?.members ?? []).map((member) => (
            <div
              key={member.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2"
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <UserRound className="text-muted-foreground size-4 shrink-0" aria-hidden />
                <span className="truncate text-sm font-medium">{member.email}</span>
                {member.is_owner && <Badge variant="muted">Owner</Badge>}
                {!member.accepted && (
                  <Badge variant="warning">
                    <Mail className="mr-1 inline size-3" aria-hidden />
                    Invited
                  </Badge>
                )}
              </div>

              {canManage && !member.is_owner && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPendingRemove(member)}
                  disabled={remove.isPending}
                  title="Remove from this account"
                >
                  <Trash2 className="size-4" />
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={pendingRemove !== null}
        title={`Remove ${pendingRemove?.email ?? ''}?`}
        description="They are signed out immediately and can no longer reach anything on this account. Nothing they ran or set up is changed or removed."
        confirmLabel="Remove"
        destructive
        busy={remove.isPending}
        error={remove.error}
        onOpenChange={(open) => !open && setPendingRemove(null)}
        onConfirm={() => {
          if (pendingRemove !== null) remove.mutate(pendingRemove);
        }}
      />
    </div>
  );
}
